import { useEffect, useRef } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { env } from '@/config/env'
import { queryKeys } from '@/api/query-keys'
import { isEkycBellNotification } from '@/features/ekyc/lib/isEkycBellNotification'
import { applySocketNotificationToCache } from '@/features/notifications/lib/apply-socket-notification'
import { useNotificationSocketStore } from '@/features/notifications/lib/notificationSocketStore'
import { resolveSpNotificationSubdomain } from '@/features/notifications/lib/spBellSubdomain'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  isDevNotificationBackendDisabled,
  isSocketUnreachableConnectError,
  markDevNotificationBackendUnavailable,
  resetNotificationDevBackendForTests,
} from '@/features/notifications/lib/notificationDevBackend'
import { logger } from '@/lib/logger'

type SharedBellSocket = { socket: Socket; key: string }

let sharedBellSocket: SharedBellSocket | null = null
let sharedBellSocketRefCount = 0

/** Clears module-level socket state. Vitest only. */
export function resetNotificationSocketSingletonForTests(): void {
  if (sharedBellSocket) {
    try {
      sharedBellSocket.socket.disconnect()
    } catch {
      /* noop */
    }
  }
  sharedBellSocket = null
  sharedBellSocketRefCount = 0
  resetNotificationDevBackendForTests()
}

function isManagerReconnecting(
  manager: Socket['io'],
): manager is Socket['io'] & { reconnecting?: boolean } {
  return Boolean((manager as { reconnecting?: boolean }).reconnecting)
}

function socketKey(wsUrl: string, subdomain: string): string {
  return `${wsUrl}\0${subdomain}`
}

function retainBellSocket(wsUrl: string, subdomain: string): Socket {
  const key = socketKey(wsUrl, subdomain)
  sharedBellSocketRefCount++
  if (sharedBellSocket?.key === key) {
    return sharedBellSocket.socket
  }
  if (sharedBellSocket) {
    sharedBellSocket.socket.disconnect()
    sharedBellSocket = null
  }
  const socket = io(wsUrl, {
    withCredentials: true,
    auth: { subdomain },
    transports: ['websocket', 'polling'],
    reconnection: import.meta.env.DEV ? !isDevNotificationBackendDisabled() : true,
    reconnectionAttempts: import.meta.env.DEV ? 5 : 3,
    reconnectionDelay: import.meta.env.DEV ? 1000 : 10_000,
  })
  sharedBellSocket = { socket, key }
  return socket
}

function releaseBellSocket(): void {
  sharedBellSocketRefCount--
  queueMicrotask(() => {
    if (sharedBellSocketRefCount <= 0 && sharedBellSocket) {
      sharedBellSocket.socket.disconnect()
      sharedBellSocket = null
    }
  })
}

function getNotificationSocketBaseUrl(): string {
  const configured = env.VITE_NOTIFICATION_SERVICE_WS_URL.trim()
  if (!globalThis.window?.location) {
    return configured
  }
  const origin = globalThis.window.location.origin
  if (!configured) {
    return origin
  }
  try {
    const cfgOrigin = new URL(configured).origin
    if (cfgOrigin !== origin && import.meta.env.DEV) {
      logger.warn('notification_socket.cross_origin_dev', {
        message:
          'Using SPA origin for Socket.io so auth cookies reach the server. Clear VITE_NOTIFICATION_SERVICE_WS_URL or point it at the SPA origin in local dev.',
        configured,
        origin,
      })
      return origin
    }
  } catch {
    return configured
  }
  return configured
}

/**
 * Socket.io bell push — invalidates SP notification queries on `notification:new`.
 * Call once from the authenticated shell layout.
 */
export function useNotificationSocket(): void {
  const userId = useSessionStore((s) => s.user?.id)
  const userSubdomain = useSessionStore((s) => s.user?.subdomain)
  const queryClient = useQueryClient()
  const socketRef = useRef<Socket | null>(null)
  const queryClientRef = useRef<QueryClient>(queryClient)
  queryClientRef.current = queryClient

  const wsUrl = getNotificationSocketBaseUrl()
  const subdomain = userId ? resolveSpNotificationSubdomain(userSubdomain) : undefined

  useEffect(() => {
    if (!wsUrl || !subdomain || !userId) return

    const socket = retainBellSocket(wsUrl, subdomain)
    socketRef.current = socket

    const invalidateNotifications = (): void => {
      Promise.resolve(
        queryClientRef.current.invalidateQueries({
          queryKey: queryKeys.spNotifications.all(),
          refetchType: 'active',
        }),
      ).catch(() => undefined)
    }

    const invalidateDashboardSideEffects = (): void => {
      Promise.resolve(
        queryClientRef.current.invalidateQueries({
          queryKey: queryKeys.spRewards.summary(),
          refetchType: 'active',
        }),
      ).catch(() => undefined)
      Promise.resolve(
        queryClientRef.current.invalidateQueries({
          queryKey: queryKeys.spInstrument.availableList(),
          refetchType: 'active',
        }),
      ).catch(() => undefined)
    }

    /** eKYC approve/deny/under_review bells — refresh profile so gates/banner update live. */
    const invalidateEkycProfileSideEffects = (): void => {
      Promise.resolve(
        queryClientRef.current.invalidateQueries({
          queryKey: queryKeys.spProfile.detail(),
          refetchType: 'active',
        }),
      ).catch(() => undefined)
      Promise.resolve(
        queryClientRef.current.invalidateQueries({
          queryKey: queryKeys.spProfile.completeness(),
          refetchType: 'active',
        }),
      ).catch(() => undefined)
    }

    const onNew = (payload?: unknown): void => {
      applySocketNotificationToCache(queryClientRef.current, payload)
      invalidateNotifications()
      // Reward/earn events arrive as notifications — refresh metrics in real-time
      invalidateDashboardSideEffects()
      if (isEkycBellNotification(payload)) {
        invalidateEkycProfileSideEffects()
      }
    }
    const onRead = (): void => {
      invalidateNotifications()
    }
    const onReadAll = (): void => {
      invalidateNotifications()
    }
    const setConnectionLost = useNotificationSocketStore.getState().setConnectionLost

    const syncConnectionLostFromSocket = (): void => {
      if (socket.connected) {
        setConnectionLost(false)
        return
      }
      if (isManagerReconnecting(socket.io)) return
      if (socket.io.opts.reconnection === false) {
        setConnectionLost(true)
      }
    }

    const onConnect = (): void => {
      setConnectionLost(false)
      logger.info('notification_socket.connected', { subdomain })
    }
    let devConnectErrorHandled = false

    const onConnectError = (err: Error): void => {
      logger.warn('notification_socket.connect_error', { err: err.message })
      // DEV only: if :4005 is down, soft-disable push + REST noise. Do NOT treat auth
      // races (Unauthorized right after login) as fatal — allow reconnection.
      if (
        import.meta.env.DEV &&
        !isDevNotificationBackendDisabled() &&
        !devConnectErrorHandled &&
        isSocketUnreachableConnectError(err)
      ) {
        devConnectErrorHandled = true
        markDevNotificationBackendUnavailable(err)
        socket.io.opts.reconnection = false
        socket.disconnect()
        // No banner — local notif service simply isn't running; REST is soft-disabled.
        setConnectionLost(false)
        return
      }
      syncConnectionLostFromSocket()
    }
    const onDisconnect = (reason: string): void => {
      logger.info('notification_socket.disconnected', { reason })
    }
    const onReconnectFailed = (): void => {
      setConnectionLost(true)
      logger.warn('notification_socket.reconnect_failed', { subdomain })
    }
    const onReconnect = (): void => {
      setConnectionLost(false)
      invalidateNotifications()
      invalidateDashboardSideEffects()
      // Catch eKYC status changes that arrived while the socket was down
      invalidateEkycProfileSideEffects()
      logger.info('notification_socket.reconnected', { subdomain })
    }

    socket.on('notification:new', onNew)
    socket.on('notification:read', onRead)
    socket.on('notification:read-all', onReadAll)
    socket.on('connect', onConnect)
    socket.on('connect_error', onConnectError)
    socket.on('disconnect', onDisconnect)
    socket.io.on('reconnect', onReconnect)
    socket.io.on('reconnect_failed', onReconnectFailed)
    syncConnectionLostFromSocket()

    return () => {
      socket.off('notification:new', onNew)
      socket.off('notification:read', onRead)
      socket.off('notification:read-all', onReadAll)
      socket.off('connect', onConnect)
      socket.off('connect_error', onConnectError)
      socket.off('disconnect', onDisconnect)
      socket.io.off('reconnect', onReconnect)
      socket.io.off('reconnect_failed', onReconnectFailed)
      setConnectionLost(false)
      releaseBellSocket()
      socketRef.current = null
    }
  }, [wsUrl, subdomain, userId])
}
